import React, { useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import {
  CIRCLE_COLORS,
  CIRCLE_LABELS,
  MANPOWER_ACTIONS,
  MANPOWER_DEFAULTERS,
  MANPOWER_INSIGHTS,
  MANPOWER_RAW,
  MANPOWER_ROOT_CAUSE_STRIP,
} from '../../data/manpowerData';
import {
  chartBaseOptions,
  EmptyState,
  ChipStrip,
  FilterSelect,
  formatLakhs,
  InsightBanner,
  InsightCard,
  KpiCard,
  palette,
  PeriodUnavailableState,
  SearchField,
  SectionTitle,
  SortButton,
  toneAccent,
} from '../dashboardPrimitives';
import PatrollerDrilldown from './PatrollerDrilldown';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const roleTone = {
  Splicer: toneAccent('blue'),
  'Asst. Splicer': toneAccent('purple'),
  Patroller: toneAccent('red'),
  FTTx: toneAccent('amber'),
  'Fibre Supervisor': toneAccent('green'),
  Labour: toneAccent('slate'),
};

function gapColor(value) {
  if (value === 0) return 'text-emerald-600';
  if (value >= 100) return 'text-red-500';
  if (value >= 40) return 'text-amber-600';
  return 'text-navy';
}

function severityTone(totalPenalty) {
  if (totalPenalty >= 1000000) return toneAccent('red');
  if (totalPenalty >= 700000) return toneAccent('orange');
  if (totalPenalty >= 400000) return toneAccent('amber');
  return toneAccent('green');
}

function utilisationColor(value) {
  if (value < 70) return palette.danger;
  if (value < 85) return palette.warning;
  if (value < 95) return palette.accent;
  return palette.success;
}

function detailSortValue(row, key) {
  switch (key) {
    case 'circle':
    case 'mp':
      return row[key];
    case 'spl_util':
      return (row.spl_avail / row.spl_req) * 100;
    default:
      return row[key] ?? 0;
  }
}

function defaulterFlag(gap) {
  if (gap >= 26) return { label: 'FULL ABSENCE', color: palette.danger };
  if (gap >= 20) return { label: 'CRITICAL', color: palette.danger };
  if (gap >= 15) return { label: 'HIGH GAP', color: palette.warning };
  return { label: 'FLAGGED', color: palette.warning };
}

export default function ManpowerGap({ selectedMonth = "Dec'25" }) {
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [detailSort, setDetailSort] = useState({ key: 'total_penalty', dir: 'desc' });
  const [search, setSearch] = useState('');
  const [defaulterSearch, setDefaulterSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [defaulterCircleFilter, setDefaulterCircleFilter] = useState('ALL');

  const filteredRows = useMemo(() => {
    return activeCircle === 'ALL' ? MANPOWER_RAW : MANPOWER_RAW.filter((row) => row.circle === activeCircle);
  }, [activeCircle]);

  const totals = useMemo(() => {
    const totalPenalty = filteredRows.reduce((sum, row) => sum + row.total_penalty, 0);
    const totalLabGap = filteredRows.reduce((sum, row) => sum + row.lab_gap, 0);
    const totalVehGap = filteredRows.reduce((sum, row) => sum + row.veh_gap, 0);
    const totalSplGap = filteredRows.reduce((sum, row) => sum + row.spl_gap, 0);
    const totalASplGap = filteredRows.reduce((sum, row) => sum + row.aspl_gap, 0);
    const zeroLabourMps = filteredRows.filter((row) => row.lab_avail === 0).length;

    return {
      totalPenalty,
      totalLabGap,
      totalVehGap,
      totalSplGap,
      totalASplGap,
      zeroLabourMps,
    };
  }, [filteredRows]);

  const circlePenalty = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.total_penalty, 0),
    );
  }, [filteredRows]);

  const resourceCategories = useMemo(() => {
    const source = filteredRows;
    return [
      { label: 'Labour', val: source.reduce((sum, row) => sum + row.lab_pen, 0), color: palette.danger },
      { label: 'Vehicle', val: source.reduce((sum, row) => sum + row.veh_pen, 0), color: palette.warning },
      { label: 'Splicer', val: source.reduce((sum, row) => sum + row.spl_pen, 0), color: palette.navy },
      { label: 'Asst. Splicer', val: source.reduce((sum, row) => sum + row.aspl_pen, 0), color: palette.slate },
      { label: 'Patroller', val: source.reduce((sum, row) => sum + row.ptr_pen, 0), color: palette.slate },
    ];
  }, [filteredRows]);

  const sortedByPenalty = useMemo(() => {
    return [...filteredRows].sort((a, b) => b.total_penalty - a.total_penalty);
  }, [filteredRows]);

  const sortedByUtilisation = useMemo(() => {
    return [...filteredRows]
      .map((row) => ({ ...row, spl_util: (row.spl_avail / row.spl_req) * 100 }))
      .sort((a, b) => a.spl_util - b.spl_util);
  }, [filteredRows]);

  const detailRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      const left = detailSortValue(a, detailSort.key);
      const right = detailSortValue(b, detailSort.key);
      if (typeof left === 'string') {
        return detailSort.dir === 'asc' ? left.localeCompare(right) : right.localeCompare(left);
      }
      return detailSort.dir === 'asc' ? left - right : right - left;
    });
    if (!search.trim()) return rows;
    const query = search.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [detailSort, filteredRows, search]);

  const defaulters = useMemo(() => {
    return MANPOWER_DEFAULTERS
      .filter((row) => roleFilter === 'ALL' || row.cat === roleFilter)
      .filter((row) => defaulterCircleFilter === 'ALL' || row.circle === defaulterCircleFilter)
      .filter((row) => {
        if (!defaulterSearch.trim()) return true;
        const query = defaulterSearch.toLowerCase();
        return row.name.toLowerCase().includes(query)
          || row.mp.toLowerCase().includes(query)
          || row.code.toLowerCase().includes(query);
      })
      .sort((a, b) => b.gap - a.gap || a.cat.localeCompare(b.cat));
  }, [defaulterCircleFilter, defaulterSearch, roleFilter]);

  const defaulterCounts = useMemo(() => {
    return MANPOWER_DEFAULTERS.reduce((accumulator, row) => {
      accumulator[row.cat] = (accumulator[row.cat] || 0) + 1;
      return accumulator;
    }, {});
  }, []);

  const deploymentGauges = useMemo(() => {
    const sum = (key) => filteredRows.reduce((total, row) => total + (row[key] || 0), 0);
    return [
      { name: 'Splicer', req: sum('spl_req'), avail: sum('spl_avail'), rate: 1500, type: 'util' },
      { name: 'Asst. Splicer', req: filteredRows.reduce((sumValue, row) => sumValue + row.aspl_gap, 0), avail: 0, rate: 1000, type: 'gap' },
      { name: 'Labour', req: sum('lab_req'), avail: sum('lab_avail'), rate: 750, type: 'util' },
      { name: 'Vehicle (FRT Veh.)', req: filteredRows.reduce((sumValue, row) => sumValue + row.veh_avail + row.veh_gap, 0), avail: sum('veh_avail'), rate: 3000, type: 'util' },
      { name: 'Patroller', req: filteredRows.reduce((sumValue, row) => sumValue + row.ptr_gap, 0), avail: 0, rate: 17000, type: 'gap' },
    ];
  }, [filteredRows]);

  const penaltyPerScope = useMemo(() => {
    return [...filteredRows]
      .map((row) => ({
        ...row,
        penPerScope: row.total_penalty / Math.max(row.inter + row.intra, 1),
      }))
      .sort((a, b) => b.penPerScope - a.penPerScope);
  }, [filteredRows]);

  const updateDetailSort = (key) => {
    setDetailSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (selectedMonth === "Feb'26") {
    return (
      <div className="space-y-6">
        <PeriodUnavailableState monthLabel={selectedMonth} />
        <PatrollerDrilldown selectedMonth={selectedMonth} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label })) }]} />

      {!filteredRows.length ? <EmptyState /> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <KpiCard label="Total Manpower Penalty" value={formatLakhs(totals.totalPenalty)} sub="72% of all contract penalties" dot={palette.danger} valueColor={palette.danger} pill="₹9,046,000" />
        <KpiCard label="Labour Shortfall (Man-Days)" value={totals.totalLabGap.toLocaleString()} sub="Penalty @ ₹750/day = ₹46.41 L" dot={palette.danger} valueColor={palette.danger} pill="51% of total penalty" />
        <KpiCard label="Vehicle Gap (Days)" value={totals.totalVehGap.toLocaleString()} sub="Penalty @ ₹3,000/day = ₹21.18 L" dot={palette.warning} valueColor={palette.warning} pill="23% of total penalty" />
        <KpiCard label="Splicer Gap (Days)" value={totals.totalSplGap.toLocaleString()} sub="Penalty @ ₹1,500/day = ₹9.81 L" dot={palette.accent} valueColor={palette.navy} pill="11% of total penalty" />
        <KpiCard label="Asst. Splicer Gap (Days)" value={totals.totalASplGap.toLocaleString()} sub="Penalty @ ₹1,000/day = ₹6.84 L" dot={palette.slate} valueColor={palette.slateDeep} pill="8% of total penalty" />
        <KpiCard label="MP's with Labour = 0" value={`${totals.zeroLabourMps} / ${filteredRows.length}`} sub="Zero labour deployed" dot={palette.warning} valueColor={palette.warning} pill="Systemic issue" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Penalty by Circle</SectionTitle>
          <p className="dashboard-note">Total manpower gap penalty breakdown by circle.</p>
          <div style={{ height: 250 }}>
            <Doughnut
              data={{
                labels: ['HP', 'KS', 'JA', 'UW'].map((circle) => `${circle} Circle`),
                datasets: [
                  {
                    data: circlePenalty,
                    backgroundColor: ['HP', 'KS', 'JA', 'UW'].map((circle) => CIRCLE_COLORS[circle]),
                    spacing: 2,
                    cutout: '68%',
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatLakhs(context.raw)} (${((context.raw / Math.max(totals.totalPenalty, 1)) * 100).toFixed(1)}%)`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Penalty by Resource Type</SectionTitle>
          <p className="dashboard-note">Which resource shortfall is costing the most?</p>
          <div style={{ height: 250 }}>
            <Doughnut
              data={{
                labels: resourceCategories.map((category) => category.label),
                datasets: [
                  {
                    data: resourceCategories.map((category) => category.val),
                    backgroundColor: resourceCategories.map((category) => category.color),
                    spacing: 2,
                    cutout: '68%',
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatLakhs(context.raw)} (${((context.raw / Math.max(totals.totalPenalty, 1)) * 100).toFixed(1)}%)`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.warning}>Resource Utilization Heatmap by Management Point</SectionTitle>
        <p className="dashboard-note">Gap days vs required, colour coded by penalty severity.</p>
        <div className="mb-4 flex flex-wrap gap-3 rounded-xl border border-app-border bg-slate-50 px-4 py-3 text-[11px] text-slate-600">
          <span className="font-semibold uppercase tracking-[0.08em] text-slate-400">Severity Index</span>
          <span className="summary-chip bg-red-50 text-red-700">Critical - ₹10L+</span>
          <span className="summary-chip bg-amber-50 text-amber-700">High - ₹7-10L</span>
          <span className="summary-chip bg-amber-50 text-amber-700">Medium - ₹4-7L</span>
          <span className="summary-chip bg-emerald-50 text-emerald-700">Low - Below ₹4L</span>
        </div>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>Circle</th>
                <th>MP</th>
                <th>Splicer</th>
                <th>Asst. Splicer</th>
                <th>Labour</th>
                <th>Vehicle</th>
                <th>Patroller</th>
                <th>Total Penalty</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {sortedByPenalty.map((row) => {
                const tone = severityTone(row.total_penalty);
                const severityLabel =
                  row.total_penalty >= 1000000 ? 'Critical' : row.total_penalty >= 700000 ? 'High' : row.total_penalty >= 400000 ? 'Medium' : 'Low';
                const heatCell = (value, toneValue) => (
                  <span className="heatmap-pill" style={{ backgroundColor: toneValue.soft, color: toneValue.text }}>
                    {value}
                  </span>
                );

                return (
                  <tr key={row.mp}>
                    <td>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: CIRCLE_COLORS[row.circle] }} />
                        <span className="mono-cell font-semibold text-slate-700">{row.circle}</span>
                      </span>
                    </td>
                    <td className="font-semibold text-slate-700">{row.mp}</td>
                    <td>{heatCell(row.spl_gap, severityTone(row.spl_pen * 6.5))}</td>
                    <td>{heatCell(row.aspl_gap, severityTone(row.aspl_pen * 8))}</td>
                    <td>{heatCell(row.lab_gap, severityTone(row.lab_pen * 3))}</td>
                    <td>{heatCell(row.veh_gap, severityTone(row.veh_pen * 1.5))}</td>
                    <td>{heatCell(row.ptr_gap, severityTone(Math.max(row.ptr_pen, 100000)))}</td>
                    <td className="mono-cell font-semibold text-slate-700">{formatLakhs(row.total_penalty)}</td>
                    <td><span className="status-badge" style={{ backgroundColor: tone.soft, color: tone.text }}>{severityLabel}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Splicer Gap by MP</SectionTitle>
          <p className="dashboard-note">Required vs Available Splicer man-days per month.</p>
          <div style={{ height: 264 }}>
            <Bar
              data={{
                labels: sortedByPenalty.map((row) => row.mp),
                datasets: [
                  { label: 'Available', data: sortedByPenalty.map((row) => row.spl_avail), backgroundColor: 'rgba(111, 168, 247, 0.6)', borderRadius: 4 },
                  { label: 'Gap', data: sortedByPenalty.map((row) => row.spl_gap), backgroundColor: 'rgba(239, 68, 68, 0.78)', borderRadius: 4 },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                plugins: { legend: { position: 'top', labels: { font: { family: 'Inter', size: 10, weight: '500' }, color: palette.slateDeep } } },
                scales: {
                  x: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.navy}>Labour Gap by MP</SectionTitle>
          <p className="dashboard-note">10 of 14 MPs have zero labour deployed - penalty @ ₹750/day per gap day.</p>
          <div style={{ height: 264 }}>
            <Bar
              data={{
                labels: sortedByPenalty.map((row) => row.mp),
                datasets: [
                  {
                    data: sortedByPenalty.map((row) => row.lab_gap),
                    backgroundColor: sortedByPenalty.map((row) => (
                      row.lab_gap > 500 ? palette.danger : row.lab_gap > 300 ? palette.warning : palette.accent
                    )),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>Vehicle Gap by MP</SectionTitle>
          <p className="dashboard-note">Highest cost per gap day (₹3,000). Srinagar & Jammu remain the worst affected.</p>
          <div style={{ height: 264 }}>
            <Bar
              data={{
                labels: sortedByPenalty.filter((row) => row.veh_gap > 0).map((row) => row.mp),
                datasets: [
                  {
                    data: sortedByPenalty.filter((row) => row.veh_gap > 0).map((row) => row.veh_gap),
                    backgroundColor: sortedByPenalty.filter((row) => row.veh_gap > 0).map((row) => (
                      row.veh_gap > 150 ? palette.danger : row.veh_gap > 80 ? palette.warning : palette.accent
                    )),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.slate}>Asst. Splicer Gap by MP</SectionTitle>
          <p className="dashboard-note">Asst. Splicer gaps carry a lower rate but a high total shortfall volume.</p>
          <div style={{ height: 264 }}>
            <Bar
              data={{
                labels: sortedByPenalty.map((row) => row.mp),
                datasets: [
                  {
                    data: sortedByPenalty.map((row) => row.aspl_gap),
                    backgroundColor: palette.slate,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>Cumulative Penalty Contribution by MP</SectionTitle>
          <p className="dashboard-note">Top MPs remain split by resource type so the underlying driver stays visible.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: sortedByPenalty.map((row) => row.mp),
                datasets: [
                  { label: 'Labour', data: sortedByPenalty.map((row) => row.lab_pen), backgroundColor: palette.danger, borderRadius: 4, stack: 'penalty' },
                  { label: 'Vehicle', data: sortedByPenalty.map((row) => row.veh_pen), backgroundColor: palette.warning, borderRadius: 4, stack: 'penalty' },
                  { label: 'Splicer', data: sortedByPenalty.map((row) => row.spl_pen), backgroundColor: palette.accent, borderRadius: 4, stack: 'penalty' },
                  { label: 'Asst. Splicer', data: sortedByPenalty.map((row) => row.aspl_pen), backgroundColor: palette.slate, borderRadius: 4, stack: 'penalty' },
                  { label: 'Patroller', data: sortedByPenalty.map((row) => row.ptr_pen), backgroundColor: palette.slate, borderRadius: 4, stack: 'penalty' },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: { position: 'top', labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
                scales: {
                  x: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                  y: { stacked: true, grid: { color: palette.border }, border: { display: false }, ticks: { callback: (value) => `₹${(value / 100000).toFixed(1)}L`, color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Splicer Utilisation % by MP</SectionTitle>
          <p className="dashboard-note">Target = 100% · red zone remains below the 80% threshold.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: sortedByUtilisation.map((row) => row.mp),
                datasets: [
                  {
                    data: sortedByUtilisation.map((row) => Math.min(row.spl_util, 110)),
                    backgroundColor: sortedByUtilisation.map((row) => utilisationColor(row.spl_util)),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: {
                    min: 0,
                    max: 115,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: { callback: (value) => `${value}%`, color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } },
                  },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.success}>Overall Resource Deployment Gauges</SectionTitle>
          <p className="dashboard-note">Deployed ÷ Required across the selected scope.</p>
          <div className="space-y-4">
            {deploymentGauges.map((resource) => {
              const gapDays = resource.type === 'gap' ? resource.req : Math.max(resource.req - resource.avail, 0);
              const pct = resource.type === 'gap' ? Math.min((gapDays / 800) * 100, 100) : (resource.avail / Math.max(resource.req, 1)) * 100;
              const fillColor = resource.type === 'gap' ? utilisationColor(100 - pct) : utilisationColor(pct);
              return (
                <div key={resource.name}>
                  <div className="mb-1 flex items-center justify-between text-[12px]">
                    <span className="font-medium text-slate-700">{resource.name}</span>
                    <span className="mono-cell text-slate-500">
                      {resource.type === 'gap'
                        ? `Gap: ${gapDays.toLocaleString()} days`
                        : `${resource.avail.toLocaleString()} / ${resource.req.toLocaleString()} · ${pct.toFixed(0)}%`}
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: fillColor }} />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Est. penalty:{' '}
                    <span className="mono-cell font-semibold text-slate-700">
                      {formatLakhs(gapDays * resource.rate)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.navySoft}>Penalty Per KM of Scope</SectionTitle>
          <p className="dashboard-note">Normalising penalty against the scope reference used in the source dashboard.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: penaltyPerScope.map((row) => row.mp),
                datasets: [
                  {
                    data: penaltyPerScope.map((row) => row.penPerScope),
                    backgroundColor: penaltyPerScope.map((row) => CIRCLE_COLORS[row.circle]),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: { grid: { color: palette.border }, border: { display: false }, ticks: { callback: (value) => `₹${Math.round(value)}`, color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                  y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.accent}>Detailed MP-Level Breakdown</SectionTitle>
            <p className="dashboard-note mb-0">Click column headers to sort. Search filters rows in real time.</p>
          </div>
          <SearchField value={search} onChange={setSearch} placeholder="Search MP..." className="sm:max-w-xs" />
        </div>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                {[
                  ['circle', 'Circle'],
                  ['mp', 'MP'],
                  ['spl_gap', 'Splicer Gap'],
                  ['aspl_gap', 'Asst. Splicer Gap'],
                  ['lab_gap', 'Labour Gap'],
                  ['veh_gap', 'Vehicle Gap'],
                  ['ptr_gap', 'Patroller Gap'],
                  ['spl_util', 'Splicer Util%'],
                  ['total_penalty', 'Total Penalty'],
                ].map(([key, label]) => (
                  <th key={key}><SortButton label={label} sortKey={key} sortState={detailSort} onSort={updateDetailSort} /></th>
                ))}
                <th>Penalty Bar</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.length ? detailRows.map((row) => {
                const util = (row.spl_avail / row.spl_req) * 100;
                const pctBar = (row.total_penalty / Math.max(...filteredRows.map((item) => item.total_penalty))) * 100;
                return (
                  <tr key={row.mp}>
                    <td>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: CIRCLE_COLORS[row.circle] }} />
                        <span className="mono-cell font-semibold text-slate-700">{row.circle}</span>
                      </span>
                    </td>
                    <td className="font-semibold text-slate-700">{row.mp}</td>
                    <td className={`mono-cell ${gapColor(row.spl_gap)}`}>{row.spl_gap}</td>
                    <td className={`mono-cell ${gapColor(row.aspl_gap)}`}>{row.aspl_gap}</td>
                    <td className={`mono-cell ${row.lab_avail === 0 ? 'text-red-500' : gapColor(row.lab_gap)}`}>{row.lab_avail === 0 ? 'ZERO' : row.lab_gap}</td>
                    <td className={`mono-cell ${gapColor(row.veh_gap)}`}>{row.veh_gap}</td>
                    <td className={`mono-cell ${gapColor(row.ptr_gap)}`}>{row.ptr_gap}</td>
                    <td className="mono-cell font-semibold" style={{ color: utilisationColor(util) }}>{util.toFixed(1)}%</td>
                    <td className="mono-cell font-semibold text-slate-700">{formatLakhs(row.total_penalty)}</td>
                    <td>
                      <div className="text-[11px] text-slate-400">₹{row.total_penalty.toLocaleString('en-IN')}</div>
                      <div className="mt-1 h-2 rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-navy" style={{ width: `${pctBar}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="10" className="py-10 text-center text-slate-400">No data for selected filters</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.danger}>Employee Defaulter List</SectionTitle>
            <p className="dashboard-note mb-0">All employees with ≥10 gap days in Dec\'25, sourced from Scrum Data & Labour Availability.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <SearchField value={defaulterSearch} onChange={setDefaulterSearch} placeholder="Search employee..." className="sm:min-w-[240px]" />
            <FilterSelect
              label="Role"
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: 'ALL', label: 'All Roles' },
                { value: 'Splicer', label: 'Splicer' },
                { value: 'Asst. Splicer', label: 'Asst. Splicer' },
                { value: 'Patroller', label: 'Patroller' },
                { value: 'FTTx', label: 'FTTx' },
                { value: 'Fibre Supervisor', label: 'Fibre Supervisor' },
                { value: 'Labour', label: 'Labour' },
              ]}
            />
            <FilterSelect
              label="Circle"
              value={defaulterCircleFilter}
              onChange={setDefaulterCircleFilter}
              options={Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(defaulterCounts).map(([role, count]) => {
            const tone = roleTone[role];
            return (
              <span key={role} className="summary-chip" style={{ backgroundColor: tone.soft, color: tone.text }}>
                {role}: {count}
              </span>
            );
          })}
          <span className="summary-chip bg-navy text-white">Total: {MANPOWER_DEFAULTERS.length}</span>
        </div>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee Name</th>
                <th>Emp Code</th>
                <th>Role</th>
                <th>Circle</th>
                <th>MP</th>
                <th>Gap Days</th>
                <th>Days Available</th>
                <th>Flag</th>
              </tr>
            </thead>
            <tbody>
              {defaulters.length ? defaulters.map((row, index) => {
                const tone = roleTone[row.cat] || toneAccent('slate');
                const flag = defaulterFlag(row.gap);
                return (
                  <tr key={`${row.code}-${index}`}>
                    <td className="mono-cell text-slate-400">{index + 1}</td>
                    <td className="font-semibold text-slate-700">{row.name}</td>
                    <td className="mono-cell">{row.code}</td>
                    <td>
                      <span className="summary-chip" style={{ backgroundColor: tone.soft, color: tone.text }}>
                        {row.cat}
                      </span>
                    </td>
                    <td className="mono-cell">{row.circle}</td>
                    <td>{row.mp}</td>
                    <td className="mono-cell font-semibold" style={{ color: flag.color }}>{row.gap} / 26</td>
                    <td className="mono-cell">{row.avail} days</td>
                    <td>
                      <span className="summary-chip" style={{ backgroundColor: `${flag.color}14`, color: flag.color }}>
                        {flag.label}
                      </span>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="9" className="py-10 text-center text-slate-400">No data for selected filters</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-6 text-amber-900">
          <strong>Threshold:</strong> ≥10 gap days out of 26 working days in Dec\'25 (≥38% absent). Vehicle defaulter data is tracked at MP-level only. Recommend HR cross-reference with attendance management system for comprehensive action.
        </div>
      </div>

      <PatrollerDrilldown selectedMonth={selectedMonth} />

      <InsightBanner chips={MANPOWER_ROOT_CAUSE_STRIP.items} />

      <div className="grid grid-cols-1 gap-4">
        {MANPOWER_INSIGHTS.map((insight) => (
          <InsightCard key={insight.title} {...insight} chips={insight.chips} />
        ))}
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <SectionTitle dot={palette.accent}>Prioritised Actions</SectionTitle>
            <p className="dashboard-note mb-0">
              Full execution of Actions 1-4 can recover an estimated <span className="font-semibold text-emerald-600">₹70-80 L</span> within the next billing cycle.
            </p>
          </div>
          <span className="summary-chip bg-emerald-50 text-emerald-700">Max Recoverable: ₹70-80 L / month</span>
        </div>
        <div className="mt-4 table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Priority</th>
                <th>What to Do - and Why It Matters</th>
                <th>Owner</th>
                <th>Circles</th>
                <th>Recoverable Penalty</th>
              </tr>
            </thead>
            <tbody>
              {MANPOWER_ACTIONS.map((row) => (
                <tr key={row.id}>
                  <td className="mono-cell font-semibold text-slate-500">{row.id}</td>
                  <td><span className="summary-chip" style={{ backgroundColor: row.level === 'critical' ? palette.redSoft : row.level === 'high' ? '#FFEDD5' : '#EDE9FE', color: row.level === 'critical' ? '#991B1B' : row.level === 'high' ? '#9A3412' : '#5B21B6' }}>{row.priority}</span></td>
                  <td>
                    <div className="text-[12px] font-semibold text-slate-700">{row.action}</div>
                    <div className="mt-1 text-[11px] leading-6 text-slate-500">{row.detail}</div>
                  </td>
                  <td>{row.owner}</td>
                  <td>{row.circles}</td>
                  <td className="mono-cell font-semibold text-emerald-600">{row.recoverable}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
